import { Inject, Injectable } from '@angular/core';

import type { ApiClient } from '@packages/shared-utils';
import { API_CLIENT } from './api-client.provider';
import type {
  ApiOrder,
  ApiOrderDocument,
  ApiOrderStatusHistoryEntry,
  OrderStatus,
  PaymentType,
  ProviderAssignmentType,
  SearchOrderRow,
  ServiceModel,
} from './api.types';

export type CreateOrderPayload = {
  conversation_id: string;
  handoff_request_id?: string | null;
  service_model: ServiceModel;
  category?: string;
  service_name?: string;
  scheduled_at?: string | null;
  scheduled_timezone?: string;
  address?: string;
  address_access_notes?: string;
  internal_notes?: string;
  payment_type?: PaymentType;
  quote_amount_cents?: number;
  estimated_duration_minutes?: number;
  // O6: provider assignment + notes applied atomically at create time.
  provider_assignment_type?: ProviderAssignmentType;
  provider_id?: string | null;
  provider_notes?: string | null;
  completion_notes?: string;
  cancelled_reason?: string;
  /** O6: client-generated UUID for idempotent create (dedupes retries). */
  client_request_id?: string;
};

export type UpdateOrderPayload = Partial<{
  service_model: ServiceModel;
  category: string;
  service_name: string;
  scheduled_at: string | null;
  scheduled_timezone: string;
  address: string;
  address_access_notes: string;
  internal_notes: string;
  payment_type: PaymentType;
  quote_amount_cents: number;
  estimated_duration_minutes: number;
  completion_notes: string;
  cancelled_reason: string;
  /** O7: the updated_at the client last saw, for optimistic concurrency. */
  expected_updated_at: string;
}>;

export type AssignProviderPayload = {
  provider_assignment_type: ProviderAssignmentType;
  provider_id?: string | null;
  provider_notes?: string | null;
};

export type SendPaymentPayload = {
  amount_cents?: number;
  currency?: string;
  description?: string;
};

@Injectable({ providedIn: 'root' })
export class OrdersApiService {
  constructor(@Inject(API_CLIENT) private readonly api: ApiClient) {}

  searchOrders(params?: {
    search?: string;
    status?: string;
    statuses?: OrderStatus[];
    cityCode?: string;
    cityCodes?: string[];
    sortBy?: 'status' | 'updated_at' | 'city_code';
    sortDir?: 'asc' | 'desc';
  }): Promise<{ items: SearchOrderRow[] }> {
    const p = new URLSearchParams();
    if (params?.search)            p.set('search',    params.search);
    if (params?.status)            p.set('status',    params.status);
    if (params?.statuses?.length)  p.set('statuses',  params.statuses.join(','));
    if (params?.cityCode)          p.set('cityCode',  params.cityCode);
    if (params?.cityCodes?.length) p.set('cityCodes', params.cityCodes.join(','));
    if (params?.sortBy)            p.set('sortBy',    params.sortBy);
    if (params?.sortDir)           p.set('sortDir',   params.sortDir);
    const qs = p.size ? `?${p.toString()}` : '';
    return this.api.get<{ items: SearchOrderRow[] }>(`/orders${qs}`);
  }

  createOrder(payload: CreateOrderPayload): Promise<ApiOrder> {
    return this.api.post<ApiOrder>('/orders', payload);
  }

  listOrdersForConversation(
    conversationId: string,
    opts?: { terminal?: boolean },
  ): Promise<{ items: ApiOrder[] }> {
    const terminal = opts?.terminal ? '?terminal=true' : '';
    return this.api.get<{ items: ApiOrder[] }>(
      `/conversations/${encodeURIComponent(conversationId)}/orders${terminal}`,
    );
  }

  getOrder(orderId: string): Promise<ApiOrder> {
    return this.api.get<ApiOrder>(`/orders/${encodeURIComponent(orderId)}`);
  }

  updateOrder(orderId: string, payload: UpdateOrderPayload): Promise<ApiOrder> {
    return this.api.patch<ApiOrder>(`/orders/${encodeURIComponent(orderId)}`, payload);
  }

  updateOrderStatus(
    orderId: string,
    status: OrderStatus,
    reason?: string,
    expectedUpdatedAt?: string,
  ): Promise<ApiOrder> {
    return this.api.patch<ApiOrder>(`/orders/${encodeURIComponent(orderId)}/status`, {
      status,
      reason,
      expected_updated_at: expectedUpdatedAt,
    });
  }

  assignProvider(orderId: string, payload: AssignProviderPayload): Promise<ApiOrder> {
    return this.api.patch<ApiOrder>(
      `/orders/${encodeURIComponent(orderId)}/provider`,
      payload,
    );
  }

  /**
   * Create/refresh a Stripe Payment Link for the order.
   * Backend is currently a stub — returned order has
   * stripe_payment_link_url populated but no real Stripe resource exists.
   */
  sendPayment(orderId: string, payload: SendPaymentPayload = {}): Promise<ApiOrder> {
    return this.api.post<ApiOrder>(
      `/orders/${encodeURIComponent(orderId)}/payment-link`,
      payload,
    );
  }

  listDocuments(orderId: string): Promise<{ items: ApiOrderDocument[] }> {
    return this.api.get<{ items: ApiOrderDocument[] }>(
      `/orders/${encodeURIComponent(orderId)}/documents`,
    );
  }

  async uploadDocument(
    orderId: string,
    file: File,
    documentKind: string = 'invoice_copy',
  ): Promise<ApiOrderDocument> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('document_kind', documentKind);

    const res = await this.api.postFormData<{ document: ApiOrderDocument }>(
      `/orders/${encodeURIComponent(orderId)}/documents`,
      form,
    );
    return res.document;
  }

  documentSignedUrl(documentId: string): Promise<{ url: string; expiresIn: number; document: ApiOrderDocument }> {
    return this.api.get<{ url: string; expiresIn: number; document: ApiOrderDocument }>(
      `/orders/documents/${encodeURIComponent(documentId)}/signed-url`,
    );
  }

  deleteDocument(documentId: string): Promise<{ ok: true }> {
    return this.api.delete<{ ok: true }>(
      `/orders/documents/${encodeURIComponent(documentId)}`,
    );
  }

  listStatusHistory(orderId: string): Promise<{ items: ApiOrderStatusHistoryEntry[] }> {
    return this.api.get<{ items: ApiOrderStatusHistoryEntry[] }>(
      `/orders/${encodeURIComponent(orderId)}/history`,
    );
  }

  pastOrdersForConversation(conversationId: string): Promise<{ items: ApiOrder[] }> {
    return this.listOrdersForConversation(conversationId, { terminal: true });
  }
}